/**
 * Every sprite by name (docs/spec/04 §1, 05 §4). Atlases are built the first time a sprite is
 * drawn and kept for the life of the page.
 */

import type { Screen } from '../screen';
import type { Atlas } from './atlas';
import { buildAtlas, rotationIndex } from './atlas';
import { BIG_BOOM, BOOM } from './booms';
import { warmCapsules } from './capsule';
import { EMPRESS, EMPRESS_ANGRY } from './empress';
import { MONARCH, MONARCH_HIT, MORPHS, MOTH, STINGER } from './enemies';
import { badgeDef, BOMB, SHOT, SHOT_PIERCE } from './items';
import { CAPTIVE, LANCER } from './ships';
import type { SpriteDef } from './types';

const EMPTY: SpriteDef = { frames: [['.']], colours: {} };

const DEFS = {
  lancer: LANCER,
  captive: CAPTIVE,
  stinger: STINGER,
  moth: MOTH,
  monarch: MONARCH,
  monarchHit: MONARCH_HIT,
  scarab: MORPHS[0] ?? EMPTY,
  mantis: MORPHS[1] ?? EMPTY,
  hornet: MORPHS[2] ?? EMPTY,
  empress: EMPRESS,
  empressAngry: EMPRESS_ANGRY,
  shot: SHOT,
  shotPierce: SHOT_PIERCE,
  bomb: BOMB,
  boom: BOOM,
  bigBoom: BIG_BOOM,
  badge1: badgeDef(1),
  badge5: badgeDef(5),
  badge10: badgeDef(10),
  badge20: badgeDef(20),
  badge30: badgeDef(30),
  badge50: badgeDef(50),
} as const satisfies Record<string, SpriteDef>;

export type SpriteId = keyof typeof DEFS;

export const SPRITE_IDS = Object.keys(DEFS) as SpriteId[];

/** The three morph sprites, by type (02 §5). */
export const MORPH_SPRITES: readonly SpriteId[] = ['scarab', 'mantis', 'hornet'];

/** Straight up: the heading the art is drawn at. */
export const UP = -Math.PI / 2;

const atlases = new Map<SpriteId, Atlas>();

export function atlasOf(id: SpriteId): Atlas {
  let atlas = atlases.get(id);
  if (!atlas) {
    atlas = buildAtlas(DEFS[id]);
    atlases.set(id, atlas);
  }
  return atlas;
}

/** A sprite's own size in pixels and how many frames it has. */
export function spriteInfo(id: SpriteId): { w: number; h: number; frames: number } {
  const def: SpriteDef = DEFS[id];
  const first = def.frames[0] ?? [];
  return { w: first[0]?.length ?? 0, h: first.length, frames: def.frames.length };
}

/**
 * Draw a sprite centred on (cx, cy). `frame` wraps. `heading` (radians, -π/2 up) turns sprites
 * that rotate and is ignored by the rest.
 */
export function drawSprite(screen: Screen, id: SpriteId, frame: number, cx: number, cy: number, heading = UP): void {
  const a = atlasOf(id);
  const f = ((Math.floor(frame) % a.frames) + a.frames) % a.frames;
  const r = rotationIndex(heading, a.rotations);
  screen.ctx.drawImage(
    a.canvas, r * a.cellW, f * a.cellH, a.cellW, a.cellH,
    Math.round(cx - a.cellW / 2), Math.round(cy - a.cellH / 2), a.cellW, a.cellH,
  );
}

export { badgesFor, BADGE_VALUES } from './items';
export { CAPSULE_LOOK, drawCapsule } from './capsule';

/**
 * Build every atlas now. Left to the first time each sprite is drawn, the turning of a 24-heading
 * sprite lands in the middle of a game: the first explosion, the first Monarch, the first capsule.
 */
export function warmSprites(): void {
  for (const id of SPRITE_IDS) atlasOf(id);
  warmCapsules();
}
